import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/system";
import { useLocation, useNavigate } from "react-router-dom";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  WasteSubmissionsForStaffQuery,
  WasteSubmissionsForStaffQueryVariables,
  SubmissionMaterializationType,
} from "generated-graphql/graphql";
import { WASTE_SUBMISSIONS_FOR_STAFF_QUERY } from "./api";
import { BulkActionsMenu } from "./BulkActionsMenu";
import {
  ADDITIONAL_FILTER_COLUMNS,
  COLUMNS,
  EXCLUDE_FILTER_COLUMNS,
  FILTER_GROUPS,
  type Row,
  getRowHref,
} from "./columns";

export const WASTE_FULFILLMENT_SEARCH_KEY = "waste-fulfillment-last-search";

interface GridSectionProps {
  defaultSearch: string;
}

export const GridSection = memo(function GridSection({
  defaultSearch,
}: GridSectionProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.search) {
      sessionStorage.setItem(WASTE_FULFILLMENT_SEARCH_KEY, location.search);
    }
  }, [location.search]);

  const omnisearchInputRef = useRef<HTMLInputElement>(null);
  const refetchRef = useRef<(() => void) | null>(null);
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);

  const handleSelectedRowsChanged = useCallback((rows: Row[]) => {
    setSelectedRows(rows);
  }, []);

  const handleRefetch = useCallback(() => {
    refetchRef.current?.();
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const { materializedSubmissionIds, selectedVirtualEventIds } = useMemo(() => {
    const materialized: string[] = [];
    const virtual: string[] = [];
    for (const row of selectedRows) {
      const isActuallyMaterialized =
        row.submissionId != null &&
        row.materializationType !== SubmissionMaterializationType.VirtualEvent;
      if (isActuallyMaterialized && row.submissionId) {
        materialized.push(row.submissionId);
      } else {
        virtual.push(row.id);
      }
    }
    return {
      materializedSubmissionIds: materialized,
      selectedVirtualEventIds: virtual,
    };
  }, [selectedRows]);

  const handleRowClick = useCallback(
    ({ row }: { row: Row }, event: React.MouseEvent) => {
      if (event.defaultPrevented) return;
      const href = getRowHref(row);
      if (!href) return;
      navigate(href);
    },
    [navigate]
  );

  const commandButtons = useMemo(
    () => [
      <Stack
        key="command-buttons"
        id="command-buttons"
        sx={{
          marginBottom: "1rem",
          marginTop: "1rem",
          flexDirection: { xs: "column", sm: "row" },
        }}
        spacing={2}
        alignItems="center"
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          marginTop={0}
          sx={{ flex: { sm: 1, lg: 2 }, alignItems: "center" }}
        >
          <BulkActionsMenu
            selectedRows={selectedRows}
            selectedIds={selectedRows.map((r) => r.id)}
            selectedSubmissionIds={materializedSubmissionIds}
            selectedVirtualEventIds={selectedVirtualEventIds}
            onSuccess={handleRefetch}
            onClearSelection={handleClearSelection}
          />
        </Stack>
      </Stack>,
    ],
    [
      selectedRows,
      materializedSubmissionIds,
      selectedVirtualEventIds,
      handleRefetch,
      handleClearSelection,
    ]
  );

  const getItems = useCallback(
    (data: WasteSubmissionsForStaffQuery) =>
      data.wasteSubmissionsForStaff.items,
    []
  );

  const getCount = useCallback(
    (data: WasteSubmissionsForStaffQuery) =>
      data.wasteSubmissionsForStaff.count,
    []
  );

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Stack
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
        spacing={2}
        px={3}
        paddingTop={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body1" color={theme.palette.text.secondary}>
          Waste Submissions
        </Typography>
      </Stack>
      <Box
        sx={{
          height: "100vh",
          pt: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <OmnisearchDataGrid<
          WasteSubmissionsForStaffQuery,
          WasteSubmissionsForStaffQueryVariables,
          Row
        >
          omnisearchInputRef={omnisearchInputRef}
          autoFocus={false}
          isURLDriven
          persistenceKey="waste-submissions-staff-v2"
          columns={COLUMNS}
          columnVisibilityModel={{}}
          commandButtons={commandButtons}
          dataQuery={WASTE_SUBMISSIONS_FOR_STAFF_QUERY}
          defaultSearch={defaultSearch}
          checkboxSelection
          disableRowSelectionOnClick
          getRowLink={getRowHref}
          onRowClick={handleRowClick}
          initialSortModel={[{ field: "deadline", sort: "asc" }]}
          getItems={getItems}
          getCount={getCount}
          noDataMessage="No waste submissions found for the current search."
          initialPageSize={50}
          additionalFilterColumns={ADDITIONAL_FILTER_COLUMNS}
          excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
          filterGroups={FILTER_GROUPS}
          wideLayout
          selectedIds={selectedRows.map((r) => r.id)}
          onSelectedRowsChanged={handleSelectedRowsChanged}
          debounceMs={800}
          fetchPolicy="network-only"
          onRefetchReady={(refetch) => {
            refetchRef.current = refetch;
          }}
          gridSx={{
            height: "100%",
            minHeight: "400px",
            maxHeight: "calc(100vh - 280px)",
          }}
        />
      </Box>
    </Paper>
  );
});
